import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiSearch,
  FiSliders,
  FiX,
} from "react-icons/fi";
import "../css/shop.scss";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";

const formatPrice = (price) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(price) || 0);

const normalize = (value) => String(value || "").trim().toLowerCase();

function ProductCard({
  product,
  favorite,
  onToggleWishlist,
  onAdd,
  added,
  onNavigate,
}) {
  const primaryImage = product.images?.[0] || product.image;
  const secondaryImage = product.images?.[1];

  return (
    <article className="shop-card">
      <div className="shop-card__media">
        <Link
          to={`/shopDetails/${product.id}`}
          onClick={onNavigate}
          aria-label={`View ${product.name}`}
        >
          <img
            className="shop-card__image shop-card__image--primary"
            src={primaryImage}
            alt={product.name}
            loading="lazy"
            decoding="async"
          />
          {secondaryImage && (
            <img
              className="shop-card__image shop-card__image--secondary"
              src={secondaryImage}
              alt=""
              loading="lazy"
              decoding="async"
              aria-hidden="true"
            />
          )}
        </Link>

        <div className="shop-card__badges">
          {product.bestSeller && <span>Best Seller</span>}
          {product.newArrival && <span>New</span>}
        </div>

        <button
          type="button"
          className={`shop-card__wishlist ${favorite ? "is-active" : ""}`}
          onClick={() => onToggleWishlist(product.id)}
          aria-label={
            favorite
              ? `Remove ${product.name} from wishlist`
              : `Add ${product.name} to wishlist`
          }
          aria-pressed={favorite}
        >
          <FiHeart aria-hidden="true" />
        </button>
      </div>

      <div className="shop-card__details">
        <div
          className="shop-card__rating"
          aria-label={`${product.rating || 5} out of 5 stars, ${
            product.reviews || 0
          } reviews`}
        >
          <span aria-hidden="true">★★★★★</span>
          <small>{product.rating || "5.0"}</small>
          <small>({product.reviews || 0})</small>
        </div>
        <Link
          className="shop-card__name"
          to={`/shopDetails/${product.id}`}
          onClick={onNavigate}
        >
          {product.name}
        </Link>
        <p className="shop-card__subtitle">{product.subtitle}</p>
        <div className="shop-card__bottom">
          <span className="shop-card__price">{formatPrice(product.price)}</span>
          <button
            type="button"
            className={added ? "is-added" : ""}
            onClick={() => onAdd(product)}
          >
            {added ? "Added" : "Add to bag"}
          </button>
        </div>
      </div>
    </article>
  );
}

function Shop() {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedHairTypes, setSelectedHairTypes] = useState([]);
  const [sortBy, setSortBy] = useState("featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [addedProductId, setAddedProductId] = useState(null);
  const filterTriggerRef = useRef(null);
  const filterPanelRef = useRef(null);
  const { addToCart, isWishlisted, toggleWishlist, wishlist = [] } = useContext(CartContext);
  const brandName = siteConfig.brandName;
  const wishlistOnly = searchParams.get("wishlist") === "true";
  const collection = searchParams.get("collection");

  useEffect(() => {
    setSearchTerm(searchParams.get("search") || "");
  }, [searchParams]);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const panel = filterPanelRef.current;
    const filterTrigger = filterTriggerRef.current;
    const previousOverflow = document.body.style.overflow;
    const focusableSelector =
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => {
      panel?.querySelector(focusableSelector)?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      const focusable = [...panel.querySelectorAll(focusableSelector)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      filterTrigger?.focus();
    };
  }, [filtersOpen]);

  const categories = useMemo(
    () =>
      [...new Set(productData.map((product) => product.category).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    []
  );

  const hairTypes = useMemo(
    () =>
      [
        ...new Set(
          productData.flatMap((product) =>
            Array.isArray(product.hairType) ? product.hairType : []
          )
        ),
      ].sort((a, b) => a.localeCompare(b)),
    []
  );

  const filteredProducts = useMemo(() => {
    const query = normalize(searchTerm);

    const matches = productData.filter((product) => {
      const searchableText = [
        product.name,
        product.subtitle,
        product.category,
        product.description,
        ...(product.tags || []),
        ...Object.values(product.attributes || {}),
        ...(product.hairType || []),
      ]
        .flat()
        .join(" ")
        .toLowerCase();

      const matchesSearch = !query || searchableText.includes(query);
      const matchesWishlist =
        !wishlistOnly || wishlist.includes(Number(product.id));
      const matchesCollection =
        collection !== "best-sellers" || Boolean(product.bestSeller);
      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.includes(product.category);
      const matchesHairType =
        selectedHairTypes.length === 0 ||
        selectedHairTypes.some((type) => product.hairType?.includes(type));

      return (
        matchesSearch &&
        matchesWishlist &&
        matchesCollection &&
        matchesCategory &&
        matchesHairType
      );
    });

    return [...matches].sort((a, b) => {
      if (sortBy === "best-selling") {
        return Number(b.reviews || 0) - Number(a.reviews || 0);
      }
      if (sortBy === "rating") {
        return Number(b.rating || 0) - Number(a.rating || 0);
      }
      if (sortBy === "price-low") {
        return Number(a.price) - Number(b.price);
      }
      if (sortBy === "price-high") {
        return Number(b.price) - Number(a.price);
      }
      if (sortBy === "newest") {
        return Number(Boolean(b.newArrival)) - Number(Boolean(a.newArrival)) || b.id - a.id;
      }

      return (
        Number(Boolean(b.bestSeller)) - Number(Boolean(a.bestSeller)) ||
        a.id - b.id
      );
    });
  }, [
    collection,
    searchTerm,
    selectedCategories,
    selectedHairTypes,
    sortBy,
    wishlist,
    wishlistOnly,
  ]);

  const activeFilterCount =
    selectedCategories.length + selectedHairTypes.length;

  const toggleFilter = (value, setter) => {
    setter((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedHairTypes([]);
  };

  const addProduct = (product) => {
    addToCart?.(
      product.id,
      1,
      product.size?.[0] || "One Size",
      product.colors?.[0] || "Original"
    );
    setAddedProductId(product.id);
    window.setTimeout(() => setAddedProductId(null), 1400);
  };

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const categoryCount = (category) =>
    productData.filter((product) => product.category === category).length;

  const hairTypeCount = (hairType) =>
    productData.filter((product) => product.hairType?.includes(hairType)).length;

  return (
    <div className="shop main shop-page">
      <main>
        <section className="shop-hero" aria-labelledby="shop-title">
          <span className="shop-hero__eyebrow">Plant-powered hair wellness</span>
          <h1 id="shop-title">
            {wishlistOnly
              ? "Your saved rituals"
              : collection === "best-sellers"
                ? "Best sellers"
                : `The ${brandName} collection`}
          </h1>
          <p>
            Discover considered routines made with purposeful botanicals,
            balanced textures and clear guidance for every hair goal.
          </p>
        </section>

        <section className="shop-catalog" aria-labelledby="shop-results-title">
          <div className="shop-toolbar">
            <div className="shop-search">
              <label htmlFor="shop-search">Search the collection</label>
              <FiSearch aria-hidden="true" />
              <input
                id="shop-search"
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search products, ingredients or goals"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <FiX aria-hidden="true" />
                </button>
              )}
            </div>

            <button
              ref={filterTriggerRef}
              type="button"
              className="shop-filter-toggle"
              onClick={() => setFiltersOpen(true)}
              aria-expanded={filtersOpen}
              aria-controls="shop-filters"
            >
              <FiSliders aria-hidden="true" />
              Filter {activeFilterCount > 0 && `(${activeFilterCount})`}
            </button>

            <div className="shop-sort">
              <label htmlFor="shop-sort">Sort by:</label>
              <div>
                <select
                  id="shop-sort"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                >
                  <option value="featured">Featured</option>
                  <option value="best-selling">Best selling</option>
                  <option value="rating">Highest rated</option>
                  <option value="price-low">Price, low to high</option>
                  <option value="price-high">Price, high to low</option>
                  <option value="newest">Newest</option>
                </select>
                <FiChevronDown aria-hidden="true" />
              </div>
            </div>
          </div>

          {(searchTerm || activeFilterCount > 0) && (
            <div className="shop-active-filters" aria-label="Active filters">
              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm("")}>
                  “{searchTerm}” <FiX aria-hidden="true" />
                </button>
              )}
              {selectedCategories.map((category) => (
                <button
                  type="button"
                  key={category}
                  onClick={() => toggleFilter(category, setSelectedCategories)}
                >
                  {category} <FiX aria-hidden="true" />
                </button>
              ))}
              {selectedHairTypes.map((hairType) => (
                <button
                  type="button"
                  key={hairType}
                  onClick={() => toggleFilter(hairType, setSelectedHairTypes)}
                >
                  {hairType} <FiX aria-hidden="true" />
                </button>
              ))}
              <button
                type="button"
                className="shop-active-filters__clear"
                onClick={clearFilters}
              >
                Clear all
              </button>
            </div>
          )}

          <div className="shop-layout">
            <button
              type="button"
              className={`shop-filter-backdrop ${filtersOpen ? "is-open" : ""}`}
              onClick={() => setFiltersOpen(false)}
              aria-label="Close filters"
              tabIndex={filtersOpen ? 0 : -1}
            />
            <aside
              ref={filterPanelRef}
              className={`shop-filters ${filtersOpen ? "is-open" : ""}`}
              id="shop-filters"
              aria-label="Product filters"
              aria-modal={filtersOpen ? "true" : undefined}
              role={filtersOpen ? "dialog" : undefined}
            >
              <div className="shop-filters__mobile-head">
                <strong>Filter</strong>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  aria-label="Close filters"
                >
                  <FiX aria-hidden="true" />
                </button>
              </div>

              <div className="shop-filters__heading">
                <h2>Filter</h2>
                {activeFilterCount > 0 && (
                  <button type="button" onClick={clearFilters}>
                    Remove all
                  </button>
                )}
              </div>

              <fieldset>
                <legend>Category</legend>
                {categories.map((category) => (
                  <label key={category}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() =>
                        toggleFilter(category, setSelectedCategories)
                      }
                    />
                    <span className="shop-checkbox" aria-hidden="true" />
                    <span>{category}</span>
                    <small>{categoryCount(category)}</small>
                  </label>
                ))}
              </fieldset>

              <fieldset>
                <legend>Hair Type</legend>
                {hairTypes.map((hairType) => (
                  <label key={hairType}>
                    <input
                      type="checkbox"
                      checked={selectedHairTypes.includes(hairType)}
                      onChange={() =>
                        toggleFilter(hairType, setSelectedHairTypes)
                      }
                    />
                    <span className="shop-checkbox" aria-hidden="true" />
                    <span>{hairType}</span>
                    <small>{hairTypeCount(hairType)}</small>
                  </label>
                ))}
              </fieldset>

              <button
                type="button"
                className="shop-filters__apply"
                onClick={() => setFiltersOpen(false)}
              >
                View {filteredProducts.length} products
              </button>
            </aside>

            <div className="shop-results">
              <div className="shop-results__heading">
                <h2 id="shop-results-title">
                  {filteredProducts.length} {filteredProducts.length === 1 ? "product" : "products"}
                </h2>
                <span aria-live="polite">
                  Showing {filteredProducts.length} of {productData.length}
                </span>
              </div>

              {filteredProducts.length > 0 ? (
                <div className="shop-grid">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      favorite={Boolean(isWishlisted?.(product.id))}
                      onToggleWishlist={(id) => toggleWishlist?.(id)}
                      onAdd={addProduct}
                      added={addedProductId === product.id}
                      onNavigate={toTop}
                    />
                  ))}
                </div>
              ) : (
                <div className="shop-empty" role="status">
                  <span>No matches</span>
                  <h3>We couldn’t find that ritual.</h3>
                  <p>
                    {wishlistOnly
                      ? "Save products with the heart icon and they will appear here."
                      : "Try another search or remove a filter to see more products."}
                  </p>
                  {wishlistOnly ? (
                    <Link to="/shop">Explore the collection</Link>
                  ) : (
                    <button type="button" onClick={clearFilters}>
                      Reset filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Shop;
